import IconButton from "components/common/IconButton";
import MultiSelect from "components/common/MultiSelect";
import React, { useEffect, useState } from "react";
import { Col, ProgressBar, Row, Spinner } from "react-bootstrap";
import { Trash } from "react-bootstrap-icons";
import DataTable from "react-data-table-component";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  addValidationError,
  handleAddPreauthorizationNotes,
} from "../config/preauthorizationSlice";
import { useAppContext } from "providers/AppProvider";
import DocumentUpload from "./AddNoteClinicalNote";
import { useSearchParams } from "react-router-dom";

import TextInput from "components/common/custom/forms/BootTextField/TextField";

import {
  compressPDF,
  toBase64,
  compressImage,
  formatFileSize,
} from "../../utils.js";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import Flex from "components/common/Flex";
import { BsDownload } from "react-icons/bs";
import moment from "moment";
import { getToday } from "helpers/utils";

const ClinicalDocumentation = ({
  control,
  setValue,
  watch,
  clearErrors,
  setError,
  errors,
}) => {
  const dispatch = useDispatch();
  const {
    stepFormData,
    preauthorizationNotes,
    investgationList,
    preauthorizationRequest,
    viewPreauthorizationInfo,
  } = useSelector((state) => state.preauthorization);

  const { settingData } = useSelector((state) => state.settings);

  const [searchParams] = useSearchParams();
  const reuse = searchParams.get("reuse") === "true";
  const edit = searchParams.get("edit") === "true";
  const followUp = searchParams.get("followup") === "true";

  const {
    config: { theme },
  } = useAppContext();

  const [originalFileName, setOriginalFileName] = useState("");
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionError, setCompressionError] = useState(null);
  const [investigationAttachmentFile, setInvestigationAttachmentFile] =
    useState(null);

  useEffect(() => {
    if (reuse || edit || followUp) {
      const clinicalDoc =
        viewPreauthorizationInfo?.provider_request?.clinical_documentation ||
        [];

      const investigation = clinicalDoc.find((d) => d.investigationresult);
      const clinicalInfo = clinicalDoc.find((d) => d.addinfo)?.addinfo;

      console.log({ clinicalInfo });

      setValue(
        "additional_information",
        watch("additional_information") || clinicalInfo
      );

      if (investigation) {
        const code = investigation.investigationresult.code;
        const attachment = investigation.investigationresult.attachment;

        const item = investgationList?.find(
          (item) => item?.investreslttype_code === code
        );
        if (item) {
          setValue("investgation_result", watch("investgation_result") || item);
        }

        setValue(
          "investgation_result_others",
          investigation?.investigationresult?.otherdesc
        );
        if (attachment) {
          const type = attachment.filename.endsWith(".pdf")
            ? "application/pdf"
            : "image/jpeg"; // Assume based on extension
          setOriginalFileName(attachment.filename);
          setValue(
            "investgation_result_attachment",
            watch("investgation_result_attachment") || {
              url: attachment.url,
              title: attachment.filename,
              contentType: type,
              creation: moment(getToday()).format("YYYY-MM-DD"),
              data: attachment?.data,
            }
          );
          setInvestigationAttachmentFile({
            type,
            url: attachment.url,
            title: attachment.filename,
            originalName: attachment.filename,
            data: attachment?.data,
            creation: moment(getToday()).format("YYYY-MM-DD"),
            size: null,
          });
        }
      }

      // Handle additional supporting info
      const additional =
        clinicalDoc.find((d) => d.additionalsupinfo)?.additionalsupinfo || [];
      console.log({ additional });

      if (additional && additional.length > 0) {
        const formattedAttachments = additional.map((att) => {
          const type = att.filename.endsWith(".pdf")
            ? "application/pdf"
            : "image/jpeg"; // Assume based on extension

          return {
            url: att.url,
            title: att.filename,
            originalName: att.filename,
            contentType: type,
            creation: moment(getToday()).format("YYYY-MM-DD"),
            data: att?.data,
          };
        });
        setValue("attachments", formattedAttachments);
      }
    } else if (settingData?.length > 0) {
      const data = settingData?.[0];
      setValue(
        "investgation_result",
        watch("investgation_result") ||
        investgationList?.find(
          (item) =>
            item?.investreslttype_code ===
            data?.fieldsetting_txt?.sup_info_invres
        )
      );
    } else if (stepFormData) {
      setValue("additional_information", stepFormData?.additional_information);
      setValue("investgation_result", stepFormData?.investgation_result);
      setValue(
        "investgation_result_others",
        stepFormData?.investgation_result_others
      );
      setValue(
        "investgation_result_attachment",
        stepFormData?.investgation_result_attachment
      );
      if (stepFormData?.investgation_result_attachment?.title) {
        setOriginalFileName(stepFormData.investgation_result_attachment.title);
        setInvestigationAttachmentFile({
          type: stepFormData.investgation_result_attachment.contentType,
          base64: stepFormData.investgation_result_attachment.data,
          title: stepFormData.investgation_result_attachment.title,
          originalName:
            stepFormData.investgation_result_attachment.originalName ||
            stepFormData.investgation_result_attachment.title,
          size: stepFormData.investgation_result_attachment.size,
        });
      }
    }
  }, [stepFormData, followUp, edit, reuse, settingData]);

  console.log("Current form attachments:", watch("attachments"));

  const handleFileChange = async (file, onChange) => {
    if (!file) {
      onChange(null);
      setOriginalFileName("");
      setCompressionProgress(0);
      setCompressionError(null);
      clearErrors("investgation_result_attachment");
      setInvestigationAttachmentFile(null);
      return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setCompressionError("Only PDF or image files are allowed");
      dispatch(
        addValidationError({
          field: "investgation_result_attachment",
          message: "Only PDF or image files are allowed",
        })
      );
      return;
    }

    setCompressionProgress(10);
    setCompressionError(null);

    try {
      let compressedFile = file;
      let originalSize = file.size;

      if (file.type === "application/pdf") {
        setCompressionProgress(40);
        compressedFile = await compressPDF(file);
        setCompressionProgress(80);
      } else if (file.type.startsWith("image/")) {
        setCompressionProgress(40);
        compressedFile = await compressImage(file);
        setCompressionProgress(80);
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (compressedFile.size > maxSize) {
        setCompressionError("Compressed file exceeds 10MB limit");
        dispatch(
          addValidationError({
            field: "investgation_result_attachment",
            message: "Compressed file exceeds 10MB limit",
          })
        );
        setCompressionProgress(0);
        return;
      }

      const base64Data = await toBase64(compressedFile);
      setCompressionProgress(90);

      const sanitizedName = file.name
        .replace(/\s+/g, "_")
        .replace(/\([^)]*\)/g, "")
        .replace(/[^\w.-]/g, "");
      const ext = sanitizedName
        .substring(sanitizedName.lastIndexOf("."))
        .toLowerCase();
      const hash = `${Date.now()}`;
      const title = `${hash}${ext}`;

      setOriginalFileName(file.name);

      const attachmentData = {
        contentType: compressedFile.type,
        title,
        originalName: file.name,
        size: compressedFile.size,
        creation: new Date().toISOString(),
        data: base64Data.split(",")[1] || base64Data, // Ensure pure base64 without prefix
      };

      onChange(attachmentData);

      setInvestigationAttachmentFile({
        type: compressedFile.type,
        base64: base64Data.split(",")[1] || base64Data,
        title,
        originalName: file.name,
        size: compressedFile.size,
        originalSize,
      });

      setCompressionProgress(100);
      clearErrors("investgation_result_attachment");
    } catch (error) {
      console.error("Compression error:", error);
      setCompressionError(
        "Error processing file: " + (error.message || "Unknown error")
      );
      dispatch(
        addValidationError({
          field: "investgation_result_attachment",
          message:
            "Error processing file: " + (error.message || "Unknown error"),
        })
      );
      setCompressionProgress(0);
    }
  };

  const handleRemoveAttachment = () => {
    setValue("investgation_result_attachment", null);
    setOriginalFileName("");
    setCompressionProgress(0);
    setCompressionError(null);
    setInvestigationAttachmentFile(null);
    clearErrors("investgation_result_attachment");
  };

  const handleView = (file) => {
    if (!file?.data) return;
    const dataUrl = `data:${file.type};base64,${file.data}`;
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <head><title>${file.originalName || "Preview"}</title></head>
          <body style="margin:0; padding:0;">
            ${file.type.includes("pdf")
          ? `<embed src="${dataUrl}" type="${file.type}" width="100%" height="100%" />`
          : `<img src="${dataUrl}" style="width:100%; height:auto; max-width:100%;" alt="Preview" />`
        }
          </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const handleDownload = (file) => {
    if (!file?.data) return;

    try {
      const byteCharacters = atob(file.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file.type });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.originalName || file.title || "document";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  return (
    <Row className="g-3">
      <Col xs={12}>
        <TextInput
          name="additional_information"
          label="Additional Information"
          multiline
          rows={3}
          control={control}
          errors={errors}
        />
      </Col>

      <Col xs={12} md={6} lg={4}>
        <label className="form-label mb-2">
          Investigation Result <span className="text-danger">*</span>
        </label>
        <Controller
          name="investgation_result"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <MultiSelect
              {...field}
              menuPlacement="auto"
              closeMenuOnSelect
              options={investgationList.map((item) => ({
                value: item.investreslttype_code,
                label: item.investreslttype_name,
              }))}
              value={
                field.value
                  ? {
                    value: field.value.investreslttype_code,
                    label: field.value.investreslttype_name,
                  }
                  : null
              }
              onChange={(selected) => {
                setValue(
                  "investgation_result",
                  selected
                    ? {
                      investreslttype_code: selected.value,
                      investreslttype_name: selected.label,
                    }
                    : null
                );
                if (selected?.value !== "IRA") {
                  setValue("investgation_result_attachment", null);
                  setOriginalFileName("");
                  setCompressionProgress(0);
                  setCompressionError(null);
                  clearErrors("investgation_result_attachment");
                  setInvestigationAttachmentFile(null);
                }
              }}
              isClearable
              formatOptionLabel={({ label }) => <div>{label}</div>}
              getOptionValue={(option) => option.value}
              placeholder="Choose ..."
            />
          )}
        />
      </Col>

      {watch("investgation_result")?.investreslttype_code === "IRA" && (
        <Col xs={12} lg={8}>
          <label className="form-label mb-2">
            Attachment <span className="text-danger">*</span>
          </label>
          <Controller
            name="investgation_result_attachment"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div>
                {!investigationAttachmentFile && (
                  <TextInput
                    name={field.name}
                    type="file"
                    control={control}
                    required={true}
                    rules={{ required: true }}
                    errors={errors}
                    onChange={(file) => handleFileChange(file, field.onChange)}
                    value={undefined} // Reset file input value
                  />
                )}
                {compressionProgress > 0 && compressionProgress < 100 && (
                  <div className="text-center mt-2">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <ProgressBar
                      now={compressionProgress}
                      label={`${compressionProgress}%`}
                      className="mt-2"
                    />
                    <p className="mt-2 mb-0">Compressing file...</p>
                  </div>
                )}
                {compressionError && (
                  <div className="text-danger mt-2 fs-10">
                    {compressionError}
                  </div>
                )}
                {field.value && compressionProgress === 100 && (
                  <div className="">
                    <small className="text-muted">
                      {investigationAttachmentFile.originalName}{" "}
                    </small>
                  </div>
                )}
              </div>
            )}
          />
          {investigationAttachmentFile && (
            <Flex alignItems="center" className="py-2 border-top mt-3">
              <div className="ms-3 flex-1">
                <h6 className="mb-0">
                  {investigationAttachmentFile.originalName}
                </h6>
              </div>
              <IconButton
                variant="primary"
                size="sm"
                onClick={() => handleView(investigationAttachmentFile)}
                className="ms-2"
              >
                <FontAwesomeIcon size={16} icon={faEye} />
              </IconButton>
              <IconButton
                variant="success"
                size="sm"
                onClick={() => handleDownload(investigationAttachmentFile)}
                className="ms-2"
              >
                <BsDownload size={16} />
              </IconButton>
              <IconButton
                variant="danger"
                size="sm"
                onClick={handleRemoveAttachment}
                className="ms-2"
              >
                <Trash size={16} />
              </IconButton>
            </Flex>
          )}
        </Col>
      )}

      {watch("investgation_result")?.investreslttype_code === "other" && (
        <Col xs={12} md={8}>
          <TextInput
            name="investgation_result_others"
            label="Investigation Result Details"
            control={control}
            errors={errors}
            rules={{
              required:
                watch("investgation_result")?.investreslttype_code === "other",
            }}
            size="sm"
            multiline
            required={
              watch("investgation_result")?.investreslttype_code === "other"
            }
            rows={1}
          />
        </Col>
      )}

      <Col xs={12}>
        <DocumentUpload
          watch={watch}
          control={control}
          setValue={setValue}
          clearErrors={clearErrors}
          setError={setError}
          errors={errors}
        />
      </Col>

      {/* {preauthorizationNotes?.length > 0 && (
        <Col xs={12} className="mt-3">
          <DataTable
            columns={columns}
            theme={theme}
            data={preauthorizationNotes}
            responsive
            dense
            noHeader
          />
        </Col>
      )} */}
    </Row>
  );
};
export default ClinicalDocumentation;
